import { m } from 'framer-motion'
import styled from 'styled-components'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, IconLightbulbAlt, Stack, Text } from '@machobear/design-system'
import { colors, breakpoints, fontWeights, zIndex } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { Tour, StepProps } from '@/components/tour'
import { useToaster } from '@/models/toaster/hooks'
import useShowZendesk from '@/hooks/use-zendesk'
import { useSidebarContext } from '@/hooks/use-sidebar-context'
import MobileIcon from './mobile-icon.svg?react'
import { buttonPosition } from './index.css'

const PlainButton = styled.button`
  position: relative;
  padding: 0;
  cursor: pointer;
  background: ${colors.blue16};
  border: none;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow:
    0px 2px 4px rgba(19, 43, 62, 0.25),
    0px 6px 6px 2px rgba(19, 43, 62, 0.1);
  transition: background-color 0.2s ease;

  &:hover,
  &:focus {
    background-color: ${colors.blue24};
  }

  &:active {
    background-color: ${colors.blue16};
  }
`

const Balloon = styled.span`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 9px;
  bottom: 46px;
  border-radius: 50%;
  height: 20px;
  min-width: 20px;
  background-color: ${colors.red48};
  padding: 0 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey96};
  font-weight: ${fontWeights.bold};

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    z-index: 1;
    right: -3px;
    top: -3px;
  }
`

type MinimizedProps = {
  setOpen: (x: boolean) => void
  count: number
}

type HighlightProps = {
  children?: React.ReactNode
  selector?: HTMLDivElement | null
}

const Highlight: React.FC<HighlightProps> = ({ children, selector }) => {
  const toaster = useToaster()
  const hide = !toaster?.highlight
  const onBeforeClose = () => {
    toaster.updateState('', { highlight: false, stowaway: false })
  }
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const { t } = useTranslation()
  const steps: StepProps = useMemo(
    () => [
      {
        selector: selector as HTMLElement,
        content: ({ next }) => (
          <Box padding="xxxxsmall" style={{ maxWidth: '225px' }} onClick={e => e.stopPropagation()}>
            <Stack space="small">
              <Text size="medium" weight="strong">
                {t('b.toaster.view.key_features.tour.title', 'Get started faster')}
              </Text>
              <Text size="small">
                {t(
                  'b.toaster.view.key_features.tour.description',
                  'Your shortcut to understanding the powerful features of liv.rent.'
                )}
              </Text>
            </Stack>
            <Box paddingTop="medium" display="flex" justifyContent="flexEnd">
              <Box style={{ minWidth: '96px' }}>
                <Button size="small" onClick={next}>
                  {t('b.toaster.view.key_features.tour.button', 'Got it!')}
                </Button>
              </Box>
            </Box>
          </Box>
        ),
        styles: {
          popover: base => ({
            ...base,
            zIndex: zIndex.modal + 1,
            background: colors.white,
            padding: '16px',
            borderRadius: '8px',
            opacity: 1,
            transition: 'none',
            boxShadow: 'none',
            animation: 'tour-opacity-popover 0.03s linear 0s 1',
            left: displayMobile ? '0px' : '17px'
          })
        }
      }
    ],
    [selector, t]
  )
  return (
    <Tour
      hide={hide || !selector}
      steps={steps}
      position="top"
      positionStrategy={displayMobile ? 'rb' : 'lb'}
      showCloseButton={false}
      onBeforeClose={onBeforeClose}
    >
      {children}
    </Tour>
  )
}

const OnboardButton = ({ setOpen, count }: MinimizedProps) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const { isSidebarOpen } = useSidebarContext()
  const title = t('b.toaster.view.get_started.title', 'Get started with liv.rent')
  const buttonRef = useRef<HTMLDivElement>(null)
  const showZendeskWidget = useShowZendesk()

  return (
    <Box
      ref={buttonRef}
      className={showZendeskWidget ? buttonPosition.widget : buttonPosition.default}
    >
      <Highlight selector={buttonRef.current}>
        {count > 0 && <Balloon>{count}</Balloon>}
        {isMobile ? (
          <PlainButton aria-label={title} onClick={() => setOpen(true)}>
            <MobileIcon width={16} height={22} />
          </PlainButton>
        ) : (
          <Box
            component="button"
            onClick={e => {
              e.stopPropagation()
              setOpen(true)
            }}
            background="white"
            width="full"
            padding="small"
            gap="xsmall"
            borderRadius="large"
            display="flex"
            justifyContent={isSidebarOpen ? 'flexStart' : 'center'}
            alignItems="center"
            cursor="pointer"
          >
            <Text>
              <IconLightbulbAlt />
            </Text>
            {isSidebarOpen && (
              <Text weight="strong" maxLines={1}>
                <m.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ type: 'spring', duration: 1 }}
                >
                  {title}
                </m.span>
              </Text>
            )}
          </Box>
        )}
      </Highlight>
    </Box>
  )
}

export default OnboardButton
